/**
 * Client Store
 * ============
 * State management for clients.
 */

import { create } from 'zustand'
import { clientService, type ClientWithStats, type ClientFilters } from '../services'
import type { Client, ClientInsert } from '../types/database.types'

export interface CreateClientOptions {
  clientData: ClientInsert
  sendInvitation?: boolean
  providerName?: string
}

interface ClientState {
  // Data
  clients: ClientWithStats[]
  selectedClient: Client | null
  isLoading: boolean
  error: string | null

  // Filters
  filters: ClientFilters
  searchQuery: string

  // Actions
  fetchClients: (providerId: string) => Promise<void>
  fetchClientById: (clientId: string) => Promise<Client | null>
  createClient: (options: CreateClientOptions) => Promise<Client>
  updateClient: (clientId: string, updates: Parameters<typeof clientService.updateClient>[1]) => Promise<Client | null>
  deleteClient: (clientId: string) => Promise<boolean>
  setSelectedClient: (client: Client | null) => void
  setFilters: (filters: Partial<ClientFilters>) => void
  setSearchQuery: (query: string) => void
  clearFilters: () => void
  activateClient: (clientId: string) => Promise<Client | null>
  archiveClient: (clientId: string) => Promise<Client | null>
  regenerateAccessToken: (clientId: string) => Promise<Client | null>
  sendClientInvitation: (client: Client, providerName: string) => Promise<{ success: boolean; error?: string }>
}

export const useClientStore = create<ClientState>((set, get) => ({
  // Initial state
  clients: [],
  selectedClient: null,
  isLoading: false,
  error: null,
  filters: {},
  searchQuery: '',

  // Fetch all clients
  fetchClients: async (providerId: string) => {
    set({ isLoading: true, error: null })
    try {
      const { filters, searchQuery } = get()
      const clients = await clientService.getClients(providerId, {
        ...filters,
        search: searchQuery || undefined,
      })
      set({ clients, isLoading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch clients',
        isLoading: false,
      })
    }
  },

  // Fetch a single client
  fetchClientById: async (clientId: string) => {
    set({ isLoading: true, error: null })
    try {
      const client = await clientService.getClientById(clientId)
      set({ selectedClient: client, isLoading: false })
      return client
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch client',
        isLoading: false,
      })
      return null
    }
  },

  // Create a new client
  createClient: async ({ clientData, sendInvitation = true, providerName }) => {
    set({ isLoading: true, error: null })
    try {
      const client = await clientService.createClient(clientData)
      set((state) => ({
        clients: [client, ...state.clients],
        isLoading: false,
      }))

      // Send invitation email if requested
      if (sendInvitation && providerName && client.email) {
        // Fire and forget - don't block on email sending
        clientService.sendAccessLinkEmail(client, providerName).catch((err) => {
          console.error('Failed to send invitation email:', err)
        })
      }

      return client
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create client',
        isLoading: false,
      })
      throw error
    }
  },

  // Update a client
  updateClient: async (clientId, updates) => {
    set({ isLoading: true, error: null })
    try {
      const updated = await clientService.updateClient(clientId, updates)
      if (updated) {
        set((state) => ({
          clients: state.clients.map((c) =>
            c.id === clientId ? { ...c, ...updated } : c
          ),
          selectedClient:
            state.selectedClient?.id === clientId
              ? { ...state.selectedClient, ...updated }
              : state.selectedClient,
          isLoading: false,
        }))
      }
      return updated
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update client',
        isLoading: false,
      })
      throw error
    }
  },

  // Delete a client
  deleteClient: async (clientId) => {
    set({ isLoading: true, error: null })
    try {
      await clientService.deleteClient(clientId)
      set((state) => ({
        clients: state.clients.filter((c) => c.id !== clientId),
        selectedClient:
          state.selectedClient?.id === clientId ? null : state.selectedClient,
        isLoading: false,
      }))
      return true
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete client',
        isLoading: false,
      })
      return false
    }
  },

  // Set selected client
  setSelectedClient: (client) => {
    set({ selectedClient: client })
  },

  // Set filters
  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
    }))
  },

  // Set search query
  setSearchQuery: (query) => {
    set({ searchQuery: query })
  },

  // Clear all filters
  clearFilters: () => {
    set({ filters: {}, searchQuery: '' })
  },

  // Activate a client
  activateClient: async (clientId) => {
    return get().updateClient(clientId, { status: 'active' })
  },

  // Archive a client
  archiveClient: async (clientId) => {
    return get().updateClient(clientId, { status: 'archived' })
  },

  // Regenerate access token
  regenerateAccessToken: async (clientId) => {
    set({ isLoading: true, error: null })
    try {
      const updated = await clientService.regenerateAccessToken(clientId)
      if (updated) {
        set((state) => ({
          clients: state.clients.map((c) =>
            c.id === clientId ? { ...c, ...updated } : c
          ),
          selectedClient:
            state.selectedClient?.id === clientId
              ? { ...state.selectedClient, ...updated }
              : state.selectedClient,
          isLoading: false,
        }))
      }
      return updated
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to regenerate token',
        isLoading: false,
      })
      throw error
    }
  },

  // Send invitation email to client
  sendClientInvitation: async (client, providerName) => {
    if (!client.email) {
      return { success: false, error: 'Client has no email address' }
    }
    if (!client.access_token) {
      return { success: false, error: 'Client has no access token' }
    }
    try {
      const result = await clientService.sendAccessLinkEmail(client, providerName)
      return result
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send invitation',
      }
    }
  },
}))
